/**
 * The KPI strip, computed by the SERVER (TEC-02 / UX-01).
 *
 * Every revenue desk used to open with three big numbers reduced in JavaScript
 * from `useEntityRows(…)` — a page capped at 500. On any deployment past 500
 * invoices, quotes or subscriptions those numbers were wrong, and wrong in the
 * worst way available: plausible, precise, and silent. The Overview was fixed
 * this way in core 0117 (`core.dashboard_measures`); the desks were missed.
 *
 * Two rules hold here, and they are the whole point of the component:
 *
 *  1. A tile's value comes from a REGISTERED measure (`core.measure`), computed
 *     under the caller's own RLS. Nothing is reduced client-side.
 *  2. A tile that cannot be computed says so. A measure that is not registered
 *     on this deployment, or that failed, renders "—" with the reason — never a
 *     zero, and never a number derived from whatever happened to be loaded.
 *
 * Rule 2 matters more than it looks: the failure this replaces produced a
 * *number*, so there was nothing for a user to notice.
 */
import { formatMoney } from '@solum/format'
import { StatStrip, type StatStripTile } from '@solum/ui'
import { useMeasures, type MeasureValue } from './queries'

export interface MeasureTile {
  /** the registered `core.measure` key, e.g. 'billing.revenue_issued' */
  key: string
  /**
   * The words the tile shows — REQUIRED (#2406). A desk words it itself; the
   * record header passes the registry row's own `label`.
   *
   * This was optional and fell back through the door's label to `key`, so any
   * measure the door did not answer was captioned with its machine key: the
   * record cap of `/e/crm_opportunity/o1` read `CRM.OPP.AMOUNT` over a dash.
   * A key is an address. Every caller already had a label to hand; making it
   * required is what stops the next one from not passing it.
   */
  label: string
  sub?: string
  accent?: boolean
}

/**
 * The registry's `format` column, as the console renders it. `money` means
 * "this is money and the registration does not say which"; an ISO 4217 code
 * (`EUR`, `USD`, …) says which, and is what the live billing/crm measures carry.
 *
 * The code is USED, not merely recognised (#1466). This function previously
 * matched `eur` and `usd` and then rendered both through the default euro
 * symbol, so a deployment whose base currency is USD read its dollars as euros
 * — the same class of silent presentation lie as a subscription count under a
 * currency symbol, which is what that issue was about. `subscriptions.mrr` is
 * registered with `core.base_currency()`, so it is the first measure whose
 * format genuinely varies per deployment.
 *
 * `decimals: 0` keeps the console's existing no-cents convention for KPI tiles;
 * `Intl` supplies the symbol and its placement.
 */
const ISO_4217 = /^[a-z]{3}$/
export function renderMeasure(m: MeasureValue | undefined): string {
  if (!m || m.value == null) return '—'
  const f = String(m.format ?? '').toLowerCase()
  if (f === 'money') return formatMoney(m.value)
  if (ISO_4217.test(f)) return formatMoney(m.value, { currency: f.toUpperCase(), decimals: 0 })
  if (f === '%' || f === 'percent') return `${Math.round(m.value)}%`
  return String(m.value)
}

/**
 * Why a tile has no value — shown as its sub-line so the gap is legible rather
 * than mysterious. Kept short: the tile is small and the detail is in the title.
 */
function why(m: MeasureValue | undefined, loading: boolean): string | undefined {
  if (loading) return 'loading…'
  if (!m) return 'measure not registered here'
  if (m.error) return 'could not be computed'
  if (m.value == null) return 'no data'
  return undefined
}

/**
 * The Console's BINDING of the shared summary strip (#2283 §2).
 *
 * The geometry and the honest-unknown rule moved to `StatStrip` in
 * `packages/ui`, which is where they belong: twelve declared shapes of the
 * same container had grown across the two hosts, and the rule that a tile which
 * cannot be computed says so is not specific to measures. What stays here is
 * the part that reads a door — `useMeasures`, the registry's `format` column,
 * and the four different ways a measure can have no value.
 *
 * The split is `ui-and-experience.md` §1's: the kit gets no data dependency,
 * and the host gets no second opinion about layout.
 */
export function MeasureStrip({ tiles, className }: { tiles: MeasureTile[]; className?: string }) {
  const q = useMeasures(tiles.map((t) => t.key))
  const byKey = new Map(((q.data ?? []) as MeasureValue[]).map((m) => [m.key, m]))

  const strip: StatStripTile[] = tiles.map((t) => {
    const m = byKey.get(t.key)
    const gap = why(m, q.isLoading)
    return {
      key: t.key,
      label: t.label,
      /* `null` rather than a rendered dash: the SHARED strip owns what an
         absent value looks like, and a host that formats its own dash is a
         second answer to the question this extraction exists to settle. */
      value: gap === undefined ? renderMeasure(m) : null,
      unavailable: gap,
      sub: t.sub,
      accent: t.accent,
    }
  })

  return <StatStrip tiles={strip} loading={q.isLoading} className={className} testId="measure-strip" />
}
